import { StudentName } from "../../../shared-components/StudentName";
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { formatDistanceToNow } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import { useApiClient } from "../../../api";
import { FruitifyLoading } from "../../../shared-components/Loading";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";
import { AssignmentUploadReviewDialog } from "./AssignmentUploadReviewDialog";
import PaginatedBucketList from "../../../shared-components/PaginatedBucketList";

const FILE_PROCESSING_BUCKETS = [
  {
    value: "PENDING",
    label: "Pending",
    countKey: "pending",
  },
  {
    value: "PROCESSING",
    label: "Processing",
    countKey: "processing",
  },
  {
    value: "NEEDS_REVIEW",
    label: "Needs Review",
    countKey: "needsReview",
  },
  {
    value: "FAILED",
    label: "Failed",
    countKey: "failed",
  },
  {
    value: "COMPLETED",
    label: "Completed",
    countKey: "completed",
  },
];

const getLatestVisibleLog = (jobResult) =>
  jobResult.job?.logs?.find((log) => !log.hideFromUser);

const getUnmappedCount = (jobResult) => {
  if (Number.isInteger(jobResult.unmappedCount)) {
    return Math.max(jobResult.unmappedCount, 0);
  }

  const totalQuestions = jobResult.assignment?.assignmentQuestions?.length || 0;

  const mappedCount =
    (jobResult.autoMappedCount || 0) + (jobResult.manualMappedCount || 0);

  return Math.max(totalQuestions - mappedCount, 0);
};

const JobStatusRow = ({ status, timestamp, needsReview = false }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
    {needsReview ? (
      <Chip label="Needs Review" color="warning" size="small" />
    ) : status === "Completed" ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Completed"
        color="success"
        size="small"
      />
    ) : status === "Failed" ? (
      <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
    ) : (
      <Chip label={status} size="small" />
    )}

    {timestamp && (
      <Typography variant="caption" color="text.secondary">
        {formatDistanceToNow(new Date(timestamp), {
          addSuffix: true,
        })}
      </Typography>
    )}
  </Stack>
);

const PendingJobCard = ({ jobResult }) => {
  const latestLog = getLatestVisibleLog(jobResult);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            <StudentName student={jobResult.student} />
          </Typography>

          <Chip label="Queued" size="small" />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {latestLog?.message || "Waiting to start..."}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ProcessingJobCard = ({ jobResult }) => {
  const progress = jobResult.job?.progress || 0;
  const latestLog = getLatestVisibleLog(jobResult);
  const currentMessage = latestLog?.message;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          <StudentName student={jobResult.student} />
        </Typography>

        <FruitifyLoading
          category={MESSAGE_CATEGORIES.aiMagic}
          size={30}
          direction="row"
        />

        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: "block",
            }}
          >
            {progress}% complete
            {currentMessage && ` - ${currentMessage}`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const CompletedJobCard = ({
  jobResult,
  needsReview,
  onAction,
  onReviewMappings,
}) => {
  const autoMappedCount = jobResult.autoMappedCount || 0;

  const manualMappedCount = jobResult.manualMappedCount || 0;

  const totalQuestions = jobResult.assignment?.assignmentQuestions?.length || 0;

  const unmappedCount = getUnmappedCount(jobResult);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              <StudentName student={jobResult.student} />
            </Typography>

            <JobStatusRow
              status={jobResult.job?.status}
              timestamp={jobResult.job?.createdAt}
              needsReview={needsReview}
            />

            <Typography variant="body2" color="text.secondary">
              {autoMappedCount} auto-mapped
              {manualMappedCount > 0 &&
                `, ${manualMappedCount} manually mapped`}{" "}
              of {totalQuestions} questions
            </Typography>

            {needsReview && unmappedCount > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="warning.main">
                  Could not find answers for {unmappedCount} question
                  {unmappedCount !== 1 ? "s" : ""}.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please confirm the student did not answer, or add the correct
                  page mappings.
                </Typography>
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {onReviewMappings && (
              <Button variant="outlined" onClick={onReviewMappings}>
                Review mappings
              </Button>
            )}
            <Button variant="contained" onClick={onAction}>
              {needsReview ? "Review" : "View"}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const FailedJobCard = ({ jobResult, onRetry }) => (
  <Card variant="outlined">
    <CardContent>
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{
          xs: "stretch",
          sm: "flex-start",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            <StudentName student={jobResult.student} />
          </Typography>

          <JobStatusRow
            status={jobResult.job?.status}
            timestamp={jobResult.job?.createdAt}
          />

          <Typography variant="body2" color="error">
            Processing failed. Please try again.
          </Typography>
        </Box>

        <Button variant="outlined" color="error" onClick={onRetry}>
          Retry Upload
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

const StudentSubmissionJobsSection = ({
  assignmentId = null,
  assignmentGroupId = null,
  refreshKey = 0,
}) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();

  const [selectedJobResult, setSelectedJobResult] = useState(null);

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);

  const baseFilters = useMemo(
    () => ({
      assignment: assignmentId,
      assignmentGroup: assignmentGroupId,
    }),
    [assignmentId, assignmentGroupId],
  );

  const handleRetry = async (jobResult, refresh) => {
    try {
      const response = await ApiClient.assignment.retryFailedUploadJob({
        fileProcessingResultId: jobResult.id,
      });

      if (response?.success) {
        enqueueSnackbar("Retrying upload processing...", {
          variant: "success",
        });

        await refresh();
      } else {
        enqueueSnackbar(
          response?.message || "Failed to retry. Please re-upload manually.",
          {
            variant: "error",
          },
        );
      }
    } catch (error) {
      console.error("Error retrying upload:", error);

      enqueueSnackbar(`Error retrying upload: ${error.message}`, {
        variant: "error",
      });
    }
  };

  const handleReview = (jobResult) => {
    setSelectedJobResult(jobResult);
    setReviewDialogOpen(true);
  };

  const handleView = (jobResult) => {
    const { assignment, student } = jobResult;

    if (
      !assignment?.id ||
      !student?.id ||
      !assignment.assignmentQuestions?.length
    ) {
      return;
    }

    const firstQuestionId = assignment.assignmentQuestions[0].id;

    navigate(
      `/assignments/answer/view/${assignment.id}/${student.id}?aq=${firstQuestionId}`,
    );
  };

  return (
    <>
      <AssignmentUploadReviewDialog
        open={reviewDialogOpen}
        setOpen={setReviewDialogOpen}
        jobResult={selectedJobResult}
        assignment={selectedJobResult?.assignment}
        onReviewed={() => {
          setReviewRefreshKey((current) => current + 1);
          setSelectedJobResult(null);
        }}
      />

      <PaginatedBucketList
        title="Student Manuscripts Processing"
        buckets={FILE_PROCESSING_BUCKETS}
        defaultBucket="PROCESSING"
        baseFilters={baseFilters}
        searchFilterName={"searchString"}
        pageSize={10}
        enabled={Boolean(assignmentId || assignmentGroupId)}
        refreshKey={refreshKey + reviewRefreshKey}
        searchPlaceholder="Search student..."
        fetchItems={({ first, after, ...filters }) =>
          ApiClient.assignment.getAssignmentUploadJobs({
            first,
            after,
            ...filters,
          })
        }
        fetchCounts={(filters) =>
          ApiClient.assignment.getAssignmentUploadJobCounts(filters)
        }
        emptyMessage={(bucket, searching) =>
          searching
            ? "No students match your search."
            : `No ${bucket.toLowerCase().replaceAll("_", " ")} results.`
        }
        renderItem={(jobResult, bucket, { refetch, refreshCounts }) => {
          switch (bucket) {
            case "PENDING":
              return <PendingJobCard jobResult={jobResult} />;

            case "PROCESSING":
              return <ProcessingJobCard jobResult={jobResult} />;

            case "NEEDS_REVIEW":
              return (
                <CompletedJobCard
                  jobResult={jobResult}
                  needsReview
                  onAction={() => handleReview(jobResult)}
                />
              );

            case "FAILED":
              return (
                <FailedJobCard
                  jobResult={jobResult}
                  onRetry={async () => {
                    await handleRetry(jobResult, refetch);

                    await refreshCounts();
                  }}
                />
              );

            case "COMPLETED":
              return (
                <CompletedJobCard
                  jobResult={jobResult}
                  needsReview={false}
                  onReviewMappings={() => handleReview(jobResult)}
                  onAction={() => handleView(jobResult)}
                />
              );

            default:
              return null;
          }
        }}
      />
    </>
  );
};

export default StudentSubmissionJobsSection;
